import Service, { type Registry as Services, service } from '@ember/service';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { task } from 'ember-concurrency';
import {
  BENI_COMPARISON_SETS,
  type ComparisonSet,
} from 'frontend-utils/utils/constants/benchmark-insight';
import * as Highcharts from 'highcharts';
import HotelBenchmarkInsightTablesCellsCellDate from 'lumen/components/hotel/benchmark-insight/tables/cells/cell-date';
import HotelBenchmarkInsightTablesCellsCellHierarchyItem from 'lumen/components/hotel/benchmark-insight/tables/cells/cell-hierarchy-item';
import HotelBenchmarkInsightTablesCellsCellValue from 'lumen/components/hotel/benchmark-insight/tables/cells/cell-value';
import HotelBenchmarkInsightTablesHeadersHeaderBeniTable from 'lumen/components/hotel/benchmark-insight/tables/headers/header-beni-table';
import type { BeniBreakdown } from 'lumen/models';
import { BREAKDOWN_PERIOD_TYPES } from 'lumen/models/benchmark-insight/breakdown';
import { isWeekendDate } from 'lumen/utils/charts/chart-utils';
import {
  BENI_COLUMN_DATA_TYPE,
  BENI_COMPARISON_SET_LONG_TRANSLATIONS,
  BENI_REFERENCE_PERIOD_SHORT_TRANSLATIONS,
  type BeniBreakdownType,
  type BeniColumnDataType,
  BREAKDOWN_TRANSLATIONS,
  BREAKDOWNS,
  FORMATTED_KPI_INDEXES,
  FORMATTED_KPIS,
  isBreakdownWithHierarchy,
  type Kpi,
  KPI_INDEX_TRANSLATIONS,
  KPI_INDEXES_FOR_KPIS,
  KPI_LONG_TRANSLATIONS,
  type NumberFormat,
} from 'lumen/utils/constants/benchmark-insight/beni-constant-values';
import { BENI_TABLE_COLUMN_WIDTHS } from 'lumen/utils/constants/benchmark-insight/beni-table-column-width-values';
import {
  BENI_TABLE_COLUMN_FORMATS,
  BENI_TABLE_COLUMN_TYPES,
  BENI_TABLE_LABELS,
  BENI_TABLE_ROW_TYPES,
  type Column,
  type ColumnConfig,
  type CombinedSegmentation,
  type DateRangeType,
  PERIOD_VALUE_PATH_BY_BREAKDOWN_PERIOD_TYPE,
  type SegmentItem,
} from 'lumen/utils/constants/benchmark-insight/beni-table-constant-values';
import {
  DROPDOWN_COMPARISON_SETS_OPTION_NONE,
  type DropdownComparisonSetsOptionNone,
} from 'lumen/utils/constants/benchmark-insight/comparison-set-selection/dropdown-comparison-sets';
import {
  DATE_AGGREGATION,
  type DateAggregationType,
} from 'lumen/utils/constants/general-constants';
import PmsMappingValueBenchmark from 'lumen/utils/datamodels/mapping/pms-mapping-value-benchmark';
import type MappingCategoryBenchmark from 'lumen/utils/datamodels/mapping-category-benchmark';

export default class BenchmarkInsightBeniTableServiceService extends Service {
  @service declare access: Services['access'];
  @service('benchmark-insight/beni-service')
  declare benchmarkInsightBeniService: Services['benchmark-insight/beni-service'];
  @service('benchmark-insight/features')
  declare benchmarkInsightFeatures: Services['benchmark-insight/features'];
  @service declare dates: Services['dates'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare simpleExport: Services['simple-export'];

  defaultJwColumnWidth = 70;
  exportTableTask = task(
    { restartable: true },
    async (
      options: {
        columns: Column[];
        dateRange: DateRangeType;
        rows: SegmentItem[];
      } &
        // `dateAggregation` is only needed when the breakdown has data aggregated by date.
        // `rootCategory` is only needed when the breakdown has data aggregated by hierarchy category.
        (| {
              breakdown: typeof BREAKDOWNS.LENGTH_OF_STAY;
              dateAggregation?: never;
              rootCategory?: never;
            }
          | {
              breakdown: typeof BREAKDOWNS.STAY_DATE | typeof BREAKDOWNS.PACE;
              dateAggregation: DateAggregationType;
              rootCategory?: never;
            }
          | {
              breakdown: typeof BREAKDOWNS.SEGMENT | typeof BREAKDOWNS.CHANNEL;
              dateAggregation?: never;
              rootCategory: MappingCategoryBenchmark | null;
            }
        ),
    ) => {
      const {
        breakdown,
        columns,
        dateAggregation,
        dateRange,
        rootCategory,
        rows,
      } = options;

      const headers: string[] = [];
      const valuePaths: string[] = [];
      const data: string[][] = [];
      // Extract headers and value paths from columns and sub-columns
      columns.forEach((column: Column) => {
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        const addHeaderAndPath = (label?: string, path?: string) => {
          if (label) {
            headers.push(label);
          }
          if (path) {
            valuePaths.push(path);
          }
        };
        if (column.valuePath) {
          addHeaderAndPath(column.name, column.valuePath);
        }
        if (column.subcolumns) {
          column.subcolumns
            .filter((subcolumn: Column) => !subcolumn.hidden)
            .forEach((subcolumn: Column) => {
              const label = column.name
                ? `${subcolumn.name} - ${column.name}`
                : subcolumn.name;
              addHeaderAndPath(label, subcolumn.valuePath);
            });
        }
      });

      let flattenedRows = rows;

      // Flatten rows if they are nested
      if (isBreakdownWithHierarchy(breakdown)) {
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        const flattenRows = (topLevelRows: SegmentItem[]) => {
          const result: SegmentItem[] = [];

          // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
          function traverse(row: SegmentItem) {
            result.push({ ...row, children: undefined });
            if (row.children) {
              row.children.forEach(traverse);
            }
          }

          topLevelRows.forEach(traverse);
          return result;
        };

        flattenedRows = flattenRows(rows);
      }

      // Map rows to data values using value paths
      flattenedRows.forEach((row: SegmentItem) => {
        const { isTotalRow } = row;
        const newRow = valuePaths.map((path: string) => {
          let value = this.deepValue(row, path);

          if (value) {
            if (dayjs.isDayjs(value)) {
              value =
                dateAggregation === DATE_AGGREGATION.WEEK
                  ? this.intl.t('general.week_of', {
                      date: Highcharts.dateFormat(
                        this.dates.highchartsLabelFormats[dateAggregation],
                        this.hotelAndMarketService.toLocalUnixTime(
                          dayjs(value).unix(),
                        ),
                      ),
                    })
                  : // Format day dates in local format, parseable by Excel
                    value.format('L');
            } else if (
              path === 'label' &&
              isBreakdownWithHierarchy(breakdown)
            ) {
              const { categoryId } = row;
              // Display the full path of the segment, e.g. "Transient > Leisure".
              if (rootCategory && categoryId) {
                const category = PmsMappingValueBenchmark.findCategoryById(
                  rootCategory.children,
                  categoryId,
                );

                if (category) {
                  const fullPathDisplayName = category.getFullPathDisplayName(
                    this.intl,
                    this.errorTracking,
                  );

                  value = fullPathDisplayName;
                }
              }
            }
          }
          if (!value && isTotalRow) {
            value = this.intl.t(
              'benchmark_insight.tables.detailed_view.row.total',
            );
          }

          return value ?? '';
        });

        data.push(newRow);
      });

      const translationString = BREAKDOWN_TRANSLATIONS[breakdown];

      const exportTitle = this.intl.t(translationString, {
        startDate: dateRange.start.format('D MMMM'),
        endDate: dateRange.end.format('D MMMM YYYY'),
      });

      await this.simpleExportTask.perform(
        headers,
        data,
        exportTitle,
        'benchmark-insight',
      );
    },
  );
  simpleExportTask = task(
    { drop: true },
    async (headers, content, title, sheetName): Promise<void> => {
      const { id } = this.hotelAndMarketService.hotelOrMarket;

      const data = JSON.stringify({
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        headers,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        content,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        title,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        sheetName,
      });

      // @ts-expect-error: Argument of type 'string | null' is not assignable to parameter of type 'string'.
      await this.simpleExport.execute(data, id);
    },
  );

  get currentLocale(): string {
    const currentLocale = this.hotelAndMarketService.user?.locale;
    if (typeof currentLocale !== 'string') {
      return 'en';
    }
    return currentLocale.split('-')[0] || 'en';
  }

  // eslint-disable-next-line max-params
  addComparisonSetBreakdownToDict(
    dict: Record<string, CombinedSegmentation>,
    breakdownRecord: BeniBreakdown,
    key:
      | 'ownComparisonSetBreakdown'
      | 'primaryComparisonSetBreakdown'
      | 'secondaryComparisonSetBreakdown',
    segmentItems: SegmentItem[],
    breakdown: BeniBreakdownType,
    maxOrder: number,
  ): void {
    const { label, date } = breakdownRecord;

    const existingDictEntry = dict[label];

    if (existingDictEntry) {
      existingDictEntry[key] = breakdownRecord;
      return;
    }

    const combinedSegmentation: CombinedSegmentation = {
      ownComparisonSetBreakdown: null,
      primaryComparisonSetBreakdown: null,
      secondaryComparisonSetBreakdown: null,
    };
    combinedSegmentation[key] = breakdownRecord;

    const isTotalRow = label.includes(BENI_TABLE_ROW_TYPES.TOTAL);

    if (breakdown === BREAKDOWNS.STAY_DATE && date && isWeekendDate(date)) {
      combinedSegmentation.className = 'table-row--highlighted';
    }

    if (isBreakdownWithHierarchy(breakdown)) {
      // Add the category id, to get the full path display name in exports
      combinedSegmentation.categoryId = label;
      const segment = segmentItems.find((el) => el.id === label) || label;
      if (typeof segment !== 'string') {
        const segmentDisplayName = segment.value;
        if (segmentDisplayName) {
          combinedSegmentation.label = segmentDisplayName;
          if (isTotalRow) {
            combinedSegmentation.isParent = true;
            combinedSegmentation.order = maxOrder + 1;
          } else {
            combinedSegmentation.order = segment?.order || 0;
            combinedSegmentation.isParent = segment?.isParent;
            combinedSegmentation.depth = segment?.depth;
          }
        }
      }
    }

    if (isTotalRow) {
      combinedSegmentation.isTotalRow = true;
      combinedSegmentation.label = this.intl.t(
        'benchmark_insight.tables.detailed_view.row.total',
      );
    } else if (breakdown === BREAKDOWNS.STAY_DATE) {
      combinedSegmentation.date = date;
    } else if (breakdown === BREAKDOWNS.LENGTH_OF_STAY) {
      combinedSegmentation.label = this.intl.t('general.los', { los: label });
    }

    dict[label] = combinedSegmentation;
  }

  addSectionBorders(kpiColumns: Column[]): Column[] {
    const styledColumns = [...kpiColumns];

    styledColumns.forEach((col) => {
      const length = col.subcolumns?.length || 0;
      if (length) {
        const lastIndex = length - 1;
        col.subcolumns?.forEach((subCol, index) => {
          const isLast = lastIndex === index;
          const isReferenceColumn =
            subCol?.period === BREAKDOWN_PERIOD_TYPES.REFERENCE;
          const isDiffColumn =
            subCol?.columnDataType === BENI_COLUMN_DATA_TYPE.DIFFERENCE;
          const isIndexColumn =
            subCol?.columnDataType === BENI_TABLE_COLUMN_FORMATS.INDEX;

          const isBorderColumn =
            isReferenceColumn || isDiffColumn || isIndexColumn || isLast;

          if (isBorderColumn) {
            subCol.hasRightBorder = true;
          }
        });
      }
    });

    return styledColumns;
  }

  deepValue(obj: object, path: string): string | Dayjs | null {
    const result = path
      .replace(/\[|\]\.?/g, '.')
      .split('.')
      .reduce((acc, val) => acc && acc[val as keyof typeof acc], obj);

    if (typeof result === 'string' || dayjs.isDayjs(result)) {
      return result;
    }

    return null;
  }

  // Needs to be injected because we pass the context to the simple export task.
  getColumns(config: ColumnConfig): Column[] {
    let columns = [];

    const dateSegmentColumn: Column = this.getDateSegmentColumn(config);
    columns.push(dateSegmentColumn);

    const kpiColumns = this.getKpiColumns(config);
    columns.push(...kpiColumns);

    columns = this.orderColumns(columns, config.secondaryComparisonSetType);
    columns = this.addSectionBorders(columns);

    return columns;
  }

  getDateSegmentColumn(config: ColumnConfig): Column {
    const { breakdown, dateAggregation, selectedRange } = config;
    const params = {
      dateAggregation,
      selectedRange,
    };

    let name: string;
    let tdComponent:
      | typeof HotelBenchmarkInsightTablesCellsCellDate
      | typeof HotelBenchmarkInsightTablesCellsCellHierarchyItem;
    let valuePath: 'date' | 'label';

    switch (breakdown) {
      case BREAKDOWNS.PACE:
      case BREAKDOWNS.STAY_DATE: {
        name = this.intl.t(BENI_TABLE_LABELS[BENI_TABLE_COLUMN_TYPES.DATE]);
        tdComponent = HotelBenchmarkInsightTablesCellsCellDate;
        valuePath = BENI_TABLE_COLUMN_TYPES.DATE;

        break;
      }

      case BREAKDOWNS.CHANNEL:
      case BREAKDOWNS.LENGTH_OF_STAY:
      case BREAKDOWNS.SEGMENT: {
        name = this.intl.t(BREAKDOWN_TRANSLATIONS[breakdown]);
        tdComponent = HotelBenchmarkInsightTablesCellsCellHierarchyItem;
        valuePath = BENI_TABLE_COLUMN_TYPES.LABEL;

        break;
      }
    }

    const width = this.getDateSegmentColumnWidth(
      dateAggregation,
      breakdown,
      this.currentLocale,
    );

    return {
      name,
      textAlign: 'left',
      isFixed: 'left',
      hasRightBorder: true,
      params,
      tdComponent,
      valuePath,
      minWidth: width,
    };
  }

  getDateSegmentColumnWidth(
    dateAggregation: DateAggregationType,
    breakdown: BeniBreakdownType,
    locale: string,
  ): number {
    if (breakdown === BREAKDOWNS.PACE) {
      return 0;
    }

    const columnWidths = BENI_TABLE_COLUMN_WIDTHS[breakdown];
    const defaultColumnWidth = 70;
    let width = defaultColumnWidth;

    switch (breakdown) {
      case BREAKDOWNS.STAY_DATE:
        width =
          columnWidths[dateAggregation as keyof typeof columnWidths][locale];
        break;
      case BREAKDOWNS.SEGMENT:
      case BREAKDOWNS.LENGTH_OF_STAY:
      case BREAKDOWNS.CHANNEL:
        width = columnWidths[locale as keyof typeof columnWidths];
        break;
      default:
        width = defaultColumnWidth;
    }
    return Number(width);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getDifferenceColumn(primaryComparisonSetType: ComparisonSet, kpi: Kpi) {
    return {
      selector: kpi,
      columnDataType: BENI_COLUMN_DATA_TYPE.DIFFERENCE,
      minWidth: 200,
      name: this.intl.t(
        BENI_TABLE_LABELS[BENI_TABLE_COLUMN_TYPES.DIFF_COMPARISON],
        {
          comparisonSet: this.intl.t(
            BENI_COMPARISON_SET_LONG_TRANSLATIONS[primaryComparisonSetType],
          ),
        },
      ),
      valuePath: '',
    };
  }

  getFinalValuePath(subColumnItem: Column, kpi: Kpi): string {
    const { selector, columnDataType, period } = subColumnItem;
    const isPickup =
      columnDataType && columnDataType === BENI_COLUMN_DATA_TYPE.PICKUP;
    const pickupIfNeeded = isPickup ? `${BENI_COLUMN_DATA_TYPE.PICKUP}.` : '';
    const formattedKpi = FORMATTED_KPIS[kpi];
    if (period) {
      const periodValuePath =
        PERIOD_VALUE_PATH_BY_BREAKDOWN_PERIOD_TYPE[period];
      if (!formattedKpi) {
        throw new Error(`Invalid formatted kpi for: ${kpi}`);
      }
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      return `${selector}.${periodValuePath}.total.${pickupIfNeeded}${formattedKpi}`;
    }
    return '';
  }

  getIndexColumn(kpi: Kpi): Column {
    const name = this.intl.t(KPI_INDEX_TRANSLATIONS[KPI_INDEXES_FOR_KPIS[kpi]]);
    return {
      selector: KPI_INDEXES_FOR_KPIS[kpi],
      name,
      columnDataType: BENI_TABLE_COLUMN_FORMATS.INDEX,
      valuePath: '',
    };
  }

  getKpiColumn(kpi: Kpi): Column {
    return {
      name: this.intl.t(KPI_LONG_TRANSLATIONS[kpi]),
      hasRightBorder: true,
      subcolumns: [],
      valuePath: '',
    };
  }

  getKpiColumns(config: ColumnConfig): Column[] {
    const {
      primaryComparisonSetType,
      secondaryComparisonSetType,
      ownComparisonSetMeta,
      primaryComparisonSetMeta,
      secondaryComparisonSetMeta,
      breakdown,
      dateAggregation,
      selectedRange,
      referencePeriod,
      visibleKpis,
    } = config;
    const kpiColumns: Column[] = [];

    visibleKpis.forEach((kpi) => {
      const kpiColumn: Column = this.getKpiColumn(kpi); // - occupancy / adr / revpar
      let valuePath = ''; // necessary for every column object

      const showRankingColumn =
        this.access.hasBenchmarkInsightRanking &&
        primaryComparisonSetType &&
        primaryComparisonSetMeta?.isVisible;

      // When the PMS integration is still being set up, we already want to show the ranking column as a teaser for the user.
      if (
        showRankingColumn ||
        !this.benchmarkInsightFeatures.hasOwnPmsIntegration
      ) {
        kpiColumn.subcolumns?.push(this.getRankingColumn(dateAggregation, kpi));
      }
      const subcolumns: Column[] = [];

      // get list of available comparison sets
      const comparisonSetTypes: ComparisonSet[] = [BENI_COMPARISON_SETS.OWN];
      if (primaryComparisonSetType && primaryComparisonSetMeta?.isVisible) {
        comparisonSetTypes.push(primaryComparisonSetType);
      }
      if (
        secondaryComparisonSetType !== DROPDOWN_COMPARISON_SETS_OPTION_NONE &&
        secondaryComparisonSetMeta?.isVisible
      ) {
        comparisonSetTypes.push(secondaryComparisonSetType);
      }

      comparisonSetTypes.forEach((comparisonSetType) => {
        const isSecondaryComparisonSetType =
          comparisonSetType === secondaryComparisonSetType;
        const hiddenFutureData =
          this.benchmarkInsightBeniService.isHiddenFutureCompsetData(
            comparisonSetType,
            selectedRange.start,
            kpi,
          );
        let selector = 'ownComparisonSetBreakdown';
        if (comparisonSetType === primaryComparisonSetType) {
          selector = 'primaryComparisonSetBreakdown';
        } else if (comparisonSetType === secondaryComparisonSetType) {
          selector = 'secondaryComparisonSetBreakdown';
        }

        if (!hiddenFutureData) {
          // The upsell icon indicates that the segmentation data is not available for other comparison sets, while
          // the user is on the BENI Light plan. They should upgrade to a trial or paid plan to access this data.
          // It would be overwhelming to show the icon in the header for all these columns, so we only show it for the first one.
          const showUpsellIcon =
            this.benchmarkInsightFeatures.showTrialTeasers &&
            breakdown === BREAKDOWNS.SEGMENT &&
            comparisonSetType === primaryComparisonSetType;

          // current period column
          subcolumns.push({
            kpi,
            selector,
            period: BREAKDOWN_PERIOD_TYPES.CURRENT,
            name: this.intl.t(
              BENI_COMPARISON_SET_LONG_TRANSLATIONS[comparisonSetType],
            ),
            comparisonSetType,
            valuePath,
            showUpsellIcon,
          });

          // pickup period column
          if (
            this.benchmarkInsightBeniService.isFutureRange(selectedRange.end) &&
            !isSecondaryComparisonSetType &&
            this.access.hasBenchmarkInsightPickupData
          ) {
            subcolumns.push({
              kpi,
              selector,
              period: BREAKDOWN_PERIOD_TYPES.CURRENT,
              name: this.intl.t('benchmark_insight.pickup'),
              columnDataType: BENI_COLUMN_DATA_TYPE.PICKUP,
              comparisonSetType,
              valuePath,
            });
          }
        }

        let metaForComparisonSet = ownComparisonSetMeta;
        if (comparisonSetType === primaryComparisonSetType) {
          metaForComparisonSet = primaryComparisonSetMeta;
        } else if (comparisonSetType === secondaryComparisonSetType) {
          metaForComparisonSet = secondaryComparisonSetMeta;
        }

        // Check that there is `hasBenchmarkInsightReferencePeriodData` access first, otherwise `referencePeriod` is null.
        if (this.access.hasBenchmarkInsightReferencePeriodData) {
          const referencePeriodDateRange =
            this.benchmarkInsightBeniService.getReferencePeriodDateRange(
              referencePeriod,
              selectedRange,
            );

          // Show the reference period column if the user has access to reference period data,
          // the comparison set type has reference period data,
          // and all of the reference period data is not hidden future compset data.
          const showReferencePeriodColumn =
            !isSecondaryComparisonSetType &&
            metaForComparisonSet?.hasReferencePeriodData &&
            !this.benchmarkInsightBeniService.isHiddenFutureCompsetData(
              comparisonSetType,
              referencePeriodDateRange.start,
              kpi,
            );

          // reference period column
          if (showReferencePeriodColumn) {
            let name = referencePeriod.toUpperCase();
            if (hiddenFutureData) {
              name = `${this.intl.t(
                BENI_COMPARISON_SET_LONG_TRANSLATIONS[comparisonSetType],
              )} ${this.intl.t(BENI_REFERENCE_PERIOD_SHORT_TRANSLATIONS[referencePeriod])}`;
            }
            subcolumns.push({
              columnDataType: BENI_COLUMN_DATA_TYPE.REFERENCE,
              selector,
              period: BREAKDOWN_PERIOD_TYPES.REFERENCE,
              name,
              comparisonSetType,
              hiddenFutureData,
              valuePath,
            });
          }
        }
      });

      const kpiSubColumns: Column[] = [];
      if (
        primaryComparisonSetType &&
        primaryComparisonSetMeta?.isVisible &&
        !this.benchmarkInsightBeniService.isHiddenFutureCompsetData(
          primaryComparisonSetType,
          selectedRange.start,
          kpi,
        )
      ) {
        // difference column;
        kpiSubColumns.push(
          this.getDifferenceColumn(primaryComparisonSetType, kpi),
        );
        // index column
        kpiSubColumns.push(this.getIndexColumn(kpi));
      }
      const newColumn: Column = {
        name: kpiColumn.name,
        hasRightBorder: kpiColumn.hasRightBorder,
        sortable: false,
        subcolumns: kpiColumn.subcolumns || [],
        valuePath,
      };

      subcolumns.forEach((subColumnItem) => {
        valuePath = this.getFinalValuePath(subColumnItem, kpi);
        const { name } = subColumnItem;
        let width = this.getKpiJwColumnWidth(subColumnItem);
        const { period, comparisonSetType } = subColumnItem;

        // Add some width for the upsell icon
        if (subColumnItem.showUpsellIcon) {
          width += 28;
        }

        newColumn.subcolumns?.push({
          name,
          textAlign: 'right',
          minWidth: width,
          columnDataType: subColumnItem.columnDataType,
          period,
          valuePath,
          tdComponent: HotelBenchmarkInsightTablesCellsCellValue,
          thComponent: HotelBenchmarkInsightTablesHeadersHeaderBeniTable,
          showUpsellIcon: subColumnItem.showUpsellIcon,
          params: {
            kpi,
            dateAggregation,
          },
          comparisonSetType,
        });
      });
      if (primaryComparisonSetType) {
        kpiSubColumns.forEach((subColumnItem) => {
          let kpiValuePath = `primaryComparisonSetBreakdown.current.total.difference.${FORMATTED_KPIS[kpi]}`;
          let valueType: BeniColumnDataType | NumberFormat =
            BENI_COLUMN_DATA_TYPE.DIFFERENCE;
          const { selector, columnDataType, name } = subColumnItem;
          const isIndex = columnDataType === BENI_TABLE_COLUMN_FORMATS.INDEX;
          if (isIndex && selector) {
            const kpiSelector = selector as keyof typeof FORMATTED_KPI_INDEXES;
            kpiValuePath = `primaryComparisonSetBreakdown.current.total.index.${FORMATTED_KPI_INDEXES[kpiSelector]}`;
            valueType = BENI_TABLE_COLUMN_FORMATS.INDEX;
          }

          const width = this.getKpiJwColumnWidth(
            subColumnItem,
            primaryComparisonSetType,
          );

          // kpi column
          newColumn.subcolumns?.push({
            textAlign: 'right',
            thComponent: HotelBenchmarkInsightTablesHeadersHeaderBeniTable,
            name,
            valuePath: kpiValuePath,
            minWidth: width,
            columnDataType,
            valueType,
            tdComponent: HotelBenchmarkInsightTablesCellsCellValue,
            hidden: false,
            params: {
              kpi,
              dateAggregation,
              primaryComparisonSetType,
            },
          });
        });
      }
      kpiColumns.push(newColumn);
    });
    return kpiColumns;
  }

  getKpiJwColumnWidth(
    column: Column,
    primaryComparisonSetType?: ComparisonSet,
  ): number {
    const { comparisonSetType, columnDataType, hiddenFutureData } = column;

    const key =
      (columnDataType ?? comparisonSetType) || BENI_COMPARISON_SETS.OWN;
    const isDiff = columnDataType === BENI_COLUMN_DATA_TYPE.DIFFERENCE;
    const isRef = columnDataType === BENI_COLUMN_DATA_TYPE.REFERENCE;
    let width = this.defaultJwColumnWidth;

    const columnWidths =
      BENI_TABLE_COLUMN_WIDTHS.KPIS[
        key as keyof typeof BENI_TABLE_COLUMN_WIDTHS.KPIS
      ];

    if (key) {
      width = isDiff
        ? parseInt(
            columnWidths[primaryComparisonSetType as keyof typeof columnWidths],
            10,
          )
        : // eslint-disable-next-line @typescript-eslint/no-base-to-string
          parseInt(columnWidths.toString(), 10);
    }

    if (isRef && hiddenFutureData) {
      width = 110;
    }

    return width;
  }

  getRankingColumn(dateAggregation: DateAggregationType, kpi: Kpi): Column {
    const { RANKING } = BENI_TABLE_COLUMN_TYPES;
    const name = this.intl.t(BENI_TABLE_LABELS[RANKING]);

    return {
      name,
      textAlign: 'right',
      valuePath: `primaryComparisonSetBreakdown.current.total.ranking.${FORMATTED_KPIS[kpi]}`,
      tdComponent: HotelBenchmarkInsightTablesCellsCellValue,
      thComponent: HotelBenchmarkInsightTablesHeadersHeaderBeniTable,
      hasRightBorder: true,
      minWidth: 100,
      valueType: BENI_TABLE_COLUMN_FORMATS.RANKING,
      params: {
        kpi,
        dateAggregation,
      },
    };
  }

  // eslint-disable-next-line max-params
  getRows(
    ownComparisonSetBreakdowns: BeniBreakdown[],
    primaryComparisonSetBreakdowns: BeniBreakdown[],
    secondaryComparisonSetBreakdowns: BeniBreakdown[],
    segmentItems: SegmentItem[],
    breakdown: BeniBreakdownType,
    // Filter than can be passed to filter out unnecessary breakdowns before combining them into rows.
    breakdownsFilterFunction?: (breakdown: BeniBreakdown) => boolean,
  ): CombinedSegmentation[] {
    const combinedSegmentationsDict: Record<string, CombinedSegmentation> = {};
    const maxOrder = Math.max(
      ownComparisonSetBreakdowns.length,
      primaryComparisonSetBreakdowns.length,
      secondaryComparisonSetBreakdowns.length,
    );

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const filterFunction = breakdownsFilterFunction ?? (() => true);

    ownComparisonSetBreakdowns
      .filter(filterFunction)
      .forEach((breakdownRecord: BeniBreakdown) =>
        this.addComparisonSetBreakdownToDict(
          combinedSegmentationsDict,
          breakdownRecord,
          'ownComparisonSetBreakdown',
          segmentItems,
          breakdown,
          maxOrder,
        ),
      );

    primaryComparisonSetBreakdowns
      .filter(filterFunction)
      .forEach((breakdownRecord: BeniBreakdown) =>
        this.addComparisonSetBreakdownToDict(
          combinedSegmentationsDict,
          breakdownRecord,
          'primaryComparisonSetBreakdown',
          segmentItems,
          breakdown,
          maxOrder,
        ),
      );

    secondaryComparisonSetBreakdowns
      .filter(filterFunction)
      .forEach((breakdownRecord: BeniBreakdown) =>
        this.addComparisonSetBreakdownToDict(
          combinedSegmentationsDict,
          breakdownRecord,
          'secondaryComparisonSetBreakdown',
          segmentItems,
          breakdown,
          maxOrder,
        ),
      );

    const combinedSegmentationsArray = Object.values(combinedSegmentationsDict);
    const result = this.orderSegmentations(
      combinedSegmentationsArray,
      breakdown,
    );

    return result ?? [];
  }

  orderColumns(
    columns: Column[],
    secondaryComparisonSetType:
      | ComparisonSet
      | DropdownComparisonSetsOptionNone,
  ): Column[] {
    columns.forEach((column) => {
      const secondaryColIndex = column.subcolumns?.findIndex((subCol) => {
        return subCol.comparisonSetType === secondaryComparisonSetType;
      });
      if (secondaryColIndex) {
        const subColumn = column.subcolumns || [];
        const removedSubColumn = subColumn.splice(secondaryColIndex, 1);
        column.subcolumns = [...subColumn, ...removedSubColumn];
      }
    });
    return columns;
  }

  orderSegmentations(
    segmentations: CombinedSegmentation[],
    breakdown: BeniBreakdownType,
  ): CombinedSegmentation[] {
    return segmentations.sort((a, b) => {
      if (!isBreakdownWithHierarchy(breakdown)) {
        const aDate = a.date ? dayjs(a.date).unix() : Infinity;
        const bDate = b.date ? dayjs(b.date).unix() : Infinity;
        return aDate - bDate;
      }

      // Move isTotalRow to the bottom
      if (a.isTotalRow !== b.isTotalRow) {
        return a.isTotalRow ? 1 : -1;
      }

      return (a.order ?? 0) - (b.order ?? 0);
    });
  }
}

declare module '@ember/service' {
  interface Registry {
    'benchmark-insight/beni-table-service': BenchmarkInsightBeniTableServiceService;
  }
}
